import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ContemporaryADUDesignDalyCity = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Contemporary ADU Design & Construction in Daly City",
    "description": "Create sleek Contemporary ADUs in Daly City featuring clean lines, open spaces, and modern aesthetics with fog-resistant materials.",
    "url": "https://www.udaadu.com/contemporary-adu-design-daly-city",
    "mainEntity": {
      "@type": "Service",
      "name": "Contemporary ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Daly City, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/contemporary-adu-design-daly-city" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Contemporary ADU Design & Construction in Daly City
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create sleek Contemporary ADUs in Daly City featuring clean lines, open spaces, and modern aesthetics with fog-resistant materials.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Contemporary ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Why Choose Contemporary ADUs in Daly City?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Climate-Smart Design</h3>
              <p className="text-muted-foreground">
                Contemporary materials and design elements are engineered to withstand Daly City's foggy coastal climate while maintaining modern aesthetics.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Maximized Space</h3>
              <p className="text-muted-foreground">
                Open floor plans and efficient layouts make the most of available space, perfect for Daly City's compact lot sizes.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Energy Efficiency</h3>
              <p className="text-muted-foreground">
                Modern insulation and smart home features reduce energy costs while providing year-round comfort in the coastal climate.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Contemporary ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Flat or shed roofs with clean lines</li>
                <li>• Large windows and sliding glass doors</li>
                <li>• Mixed materials: steel, glass, concrete</li>
                <li>• Geometric forms and asymmetrical designs</li>
                <li>• Outdoor living spaces and decks</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Open-concept living areas</li>
                <li>• High ceilings and abundant natural light</li>
                <li>• Minimalist design and built-in storage</li>
                <li>• Modern kitchens with premium appliances</li>
                <li>• Smart home technology integration</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Daly City Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Coastal Climate Adaptations</h3>
              <p className="text-muted-foreground">
                Daly City's foggy conditions require moisture-resistant materials and proper ventilation systems. Contemporary designs incorporate these needs seamlessly.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Wind and Weather Protection</h3>
              <p className="text-muted-foreground">
                Strategic placement of windows and outdoor spaces protects against prevailing winds while maximizing natural light during clear days.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-white">$220,000 - $380,000</p>
            <p className="text-muted-foreground">
              Contemporary ADUs offer excellent value through efficient design and modern construction methods. Costs vary based on size and finish level.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-white">Start Your Contemporary ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to add modern living space to your Daly City property? Let's discuss your contemporary ADU vision.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-white">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/modern-adu-design-daly-city">Modern ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/minimalist-adu-design-daly-city">Minimalist ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/industrial-adu-design-daly-city">Industrial ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default ContemporaryADUDesignDalyCity;